import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import {
  Table,
  Tab,
  Button,
  Loader,
  FiltersSlide,
  Pagination
} from 'ypf-pas-general-ui-components';
import { BsChevronLeft, BsDownload } from 'react-icons/bs';
import { MdFilterAlt } from 'react-icons/md';

import NotFoundData from '../../../components/NotFoundData/NotFoundData';
import { checkRol } from '../../../security';
import {
  proveedorAdministrador,
  proveedorNotificador,
  notificador,
  solicitante,
  consultaServicios,
  inspector
} from '../../../security/constantsRols';
import './ServiceRequestGrilla.scss';
import { useFilterContext } from '../../../context/FilterContext';
import { apiCertificationsApp } from '../../../resources/environmentConstants';
import { getUrlWithSessionCreds } from '../../../utils/globalFunctions';
import { postReportExcel } from '../../../services/serviceRequest';
import useToast from '../../../hooks/useToast';

import useSdsGrilla from './useSdsGrilla';
import { columns as getColumns, columnsAlternative as getColumnsAlter } from './columns';
import ModalDetailsIO from './ModalDetailsIO/ModalDetailsIO';

const mapGrillaAgrupada = {
  PENDIENTES: 1,
  'EN CURSO': 2,
  FINALIZADAS: 3
};

const getTabsForRoles = (completedPath) => {
  let tabsAvailables = ['PENDIENTES', 'EN CURSO', 'FINALIZADAS'];

  if (checkRol(proveedorNotificador) || checkRol(notificador) || checkRol(inspector)) {
    tabsAvailables = completedPath ? ['FINALIZADAS'] : ['EN CURSO', 'FINALIZADAS'];
  }
  if (
    (checkRol(proveedorNotificador) && checkRol(proveedorAdministrador)) ||
    checkRol(solicitante) ||
    checkRol(consultaServicios)
  ) {
    tabsAvailables = completedPath ? ['FINALIZADAS'] : ['PENDIENTES', 'EN CURSO', 'FINALIZADAS'];
  }

  return tabsAvailables;
};

const ServiceRequestGrilla = ({ setShowFilters, isFilterPinned }) => {
  const {
    data,
    loading,
    pageCount,
    tab,
    setTab,
    fetchData,
    initialPageSize,
    savedSortBy,
    pageInd,
    goToPage,
    nextPage,
    previousPage
  } = useSdsGrilla();
  const { filter, handleDeleteChips, handleChips } = useFilterContext();
  const { setNewToast } = useToast();
  const history = useHistory();
  const completedPath = history.location.pathname === '/inspector/service';

  const tabsAvailables = getTabsForRoles(completedPath);

  const stringTabFromLS = (valor) => {
    const stringFound = Object.keys(mapGrillaAgrupada).find(
      (key) => mapGrillaAgrupada[key] === valor
    );

    return stringFound || null;
  };

  const [activeTab, setActiveTab] = useState(() => {
    const tabFromLocalStorage = +localStorage.getItem('tab');

    return stringTabFromLS(tabFromLocalStorage) || tabsAvailables[0];
  });

  const [mapFilters, setMapFilters] = useState([]);
  const [showDetailIOModal, setShowDetailIOModal] = useState(false);
  const [infoRowSelected, setInfoRowSelected] = useState(null);

  const deleteFilter = (filterObj) => {
    const newMapFilters = mapFilters.filter(({ id }) => id !== filterObj?.id);

    setMapFilters(newMapFilters);
    handleDeleteChips(filterObj);
  };

  useEffect(() => {
    setTab(mapGrillaAgrupada[activeTab]);
  }, [activeTab, setTab]);

  useEffect(() => {
    setMapFilters(handleChips());
  }, [filter, handleChips]);

  const tabActive = `tab${tabsAvailables.indexOf(activeTab)}`;

  const [state] = useState({
    pageIndex: pageInd,
    pageSize: initialPageSize,
    sortBy: savedSortBy
  });

  const shouldShowDownloadButton =
    checkRol(proveedorAdministrador) &&
    (activeTab === 'FINALIZADAS' || activeTab === 'EN CURSO') &&
    data?.length !== 0;

  const handleReportDownload = async () => {
    try {
      setNewToast({
        type: 'INFO',
        message: 'Sus archivos se están descargando. Por favor, no actualice la página.'
      });

      const body = {
        NumeroSolicitudServicio:
          filter.numeroSolicitud === ''
            ? null
            : filter.numeroSolicitud?.map((numSol) => numSol?.nombre || numSol?.label),
        ubicaciones: filter.ubicacion.map((el) => el.label),
        tipoUbicacionEnum: filter.tipoUbicacionEnum?.id || null,
        servicioIds: filter.idsServicios?.map((el) => Number(el.id)),
        idUsuarioCreador: filter.idUsuarioCreador ? Number(filter.idUsuarioCreador.id) : null,
        idEvento: filter.idEvento?.id || null,
        contratoIds: filter.idsContratos?.map((el) => Number(el.id)),
        fechaDesde: filter.fechaDesde === '' ? null : filter.fechaDesde,
        fechaHasta: filter.fechaHasta === '' ? null : filter.fechaHasta,
        unidadesNegocioIds: filter.unidadesNegocioIds?.map((el) => Number(el.id)),
        grillaAgrupadaEnum: tab,
        idPrioridad: filter.idPrioridad?.id || null,
        operacionesIds: filter.operacionesIds?.map((el) => Number(el.id))
      };

      await postReportExcel(body);

      setNewToast({
        type: 'SUCCESS',
        message: 'Sus archivos han sido descargados!'
      });
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const handlePageSizeChange = (pageSize) => {
    fetchData({ pageIndex: 0, pageSize, sortBy: savedSortBy });
  };

  const handleSortAndFetch = (values) => {
    fetchData({
      pageIndex: values.pageIndex,
      pageSize: initialPageSize || values.pageSize || 10,
      sortBy: values.sortBy
    });
  };
  const handleClickTypeIO = (row) => {
    setInfoRowSelected(row);
    setShowDetailIOModal((oldValue) => !oldValue);
  };

  const clos = getColumns(handleClickTypeIO);
  const closAlter = getColumnsAlter(handleClickTypeIO);

  return (
    <>
      <div className="content-title-tabs">
        <div className="content-button-title">
          {completedPath && (
            <a
              aria-label="Volver a la grilla de inspecciones"
              className="link-button-back"
              href={getUrlWithSessionCreds(`${apiCertificationsApp}/#/inspeccion/partes`)}
            >
              <BsChevronLeft />
            </a>
          )}
          {!isFilterPinned && (
            <Button
              className={mapFilters.length === 0 ? 'btn-filter-inactive' : 'btn-filter-active'}
              colorIcon="primary"
              icon={MdFilterAlt}
              onClick={() => setShowFilters(true)}
              sizeIcon="20px"
              type="button"
              variant="white"
              widthButton="40px"
            />
          )}
          <h2 className="pas-title">
            {completedPath ? 'Bandeja de Solicitudes Finalizadas' : 'Bandeja de Solicitudes'}
          </h2>
        </div>
        {!completedPath && (
          <Tab
            activeTab={tabActive}
            onClick={(e) => {
              setActiveTab(tabsAvailables[e.target.value]);
              localStorage.setItem('page', 0);
            }}
            tabs={tabsAvailables}
          />
        )}
      </div>
      <div className="position-relative" style={{ backgroundColor: '#fff' }}>
        {mapFilters.length ? (
          <FiltersSlide
            arrayFilters={mapFilters}
            className="filters-chips"
            onDelete={(filterObj) => deleteFilter(filterObj)}
          />
        ) : null}
        {loading && <Loader show text="CARGANDO ..." />}
        {data.length === 0 && <NotFoundData />}
        {shouldShowDownloadButton && (
          <div className="d-flex justify-content-end align-items-center download-button-container p-3">
            <Button
              colorIcon="info"
              fontSizeLabel="16px"
              icon={BsDownload}
              iconRight
              label="Descargar reporte general de proveedor"
              onClick={handleReportDownload}
              sizeIcon="18px"
              type="button"
              variant="light-gray"
              widthButton="362px"
            />
          </div>
        )}
        {data.length > 0 && (
          <>
            <Table
              cantFiles={[10, 25, 50, 100]}
              columns={activeTab === 'PENDIENTES' ? clos : closAlter}
              data={data}
              fetchData={handleSortAndFetch}
              initialState={{
                pageIndex: state.pageIndex,
                pageSize: state.pageSize,
                sortBy: state.sortBy
              }}
              pageCount={pageCount}
              sortOrder
            />
            <Pagination
              cantFiles={[10, 25, 50, 100]}
              className="my-3"
              explicitSize
              gotoPage={goToPage}
              nextPage={nextPage}
              pageIndex={pageInd}
              previousPage={previousPage}
              setPageSize={handlePageSizeChange}
              size={initialPageSize}
              totalPages={pageCount}
            />
          </>
        )}
      </div>
      <ModalDetailsIO
        infoRowSelected={infoRowSelected}
        setShowDetailIOModal={setShowDetailIOModal}
        showDetailIOModal={showDetailIOModal}
      />
    </>
  );
};

ServiceRequestGrilla.propTypes = {
  setShowFilters: PropTypes.func.isRequired,
  isFilterPinned: PropTypes.bool.isRequired
};

export default ServiceRequestGrilla;
